import { Badge, Button, Card, Separator, Skeleton } from "@attmos/flowstack-ui";
import {
  Fingerprint,
  Key,
  Loader2,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
} from "lucide-react";
import * as React from "react";

import {
  useAddPasskey,
  useDeletePasskey,
  useMFAFactors,
  usePasskeys,
  useTOTPDisable,
  type Passkey,
} from "@/api/mfa";
import { cn } from "@/lib/utils";

import { TotpEnrollDialog } from "./TotpEnrollDialog";

/**
 * Security section for Settings — mirrors orbit's Settings → Security
 * page (#597 Phase 3).
 *
 * Two cards inside a single section: **Passkeys** (WebAuthn) at the
 * top with the "Recommended" badge (matches orbit's copy — passkeys
 * are phishing-resistant so we lead with them) and **Authenticator
 * app** (TOTP) below. Both consume the BFF proxy endpoints from
 * :mod:`@/api/mfa` — the SPA never touches Cognito directly.
 *
 * Loading + error posture mirrors ``TrustedBrowserSection``: skeleton
 * while the factors query is in-flight, retry affordance on error
 * (a security-adjacent control must not silently vanish on a
 * transient error — Rupert PR-596 r0), full render once loaded.
 */
export function SecuritySection(): JSX.Element {
  const factors = useMFAFactors();

  if (factors.isLoading) {
    return (
      <Card as="section" aria-labelledby="security-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="security-heading" title="Security" />
        <Skeleton className="h-32 w-full" />
      </Card>
    );
  }
  if (factors.isError || !factors.data) {
    return (
      <Card as="section" aria-labelledby="security-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="security-heading" title="Security" />
        <p className="text-sm text-muted-foreground" role="alert">
          Couldn&apos;t load MFA status.
        </p>
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => void factors.refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" aria-labelledby="security-heading" className="flex flex-col gap-4 p-card">
      <SectionHeading id="security-heading" title="Security" />
      <p className="text-sm text-muted-foreground">
        Add a second factor so a stolen password isn&apos;t enough to sign in. Passkeys are the
        preferred choice — they&apos;re phishing-resistant and use your device&apos;s biometric.
      </p>
      <PasskeysCard />
      <Separator />
      <AuthenticatorCard totpEnabled={factors.data.totp_enabled} />
    </Card>
  );
}

// --- Passkeys card ------------------------------------------------------

function PasskeysCard(): JSX.Element {
  const passkeys = usePasskeys();
  const add = useAddPasskey();

  const busy = add.isPending;
  const hasWebAuthn =
    typeof window !== "undefined" && typeof window.PublicKeyCredential !== "undefined";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-md border border-border-subtle bg-surface-2 p-2 text-muted-foreground">
            <Fingerprint className="h-4 w-4" aria-hidden />
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-foreground">Passkeys</h3>
              <Badge
                variant="outline"
                className="border-cyan/60 bg-cyan/10 text-[10px] uppercase tracking-wider text-cyan"
              >
                Recommended
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Use your device (Touch ID, Windows Hello) or a security key. Nothing to remember, and
              a phishing site can&apos;t use them.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || !hasWebAuthn}
          onClick={() => add.mutate()}
        >
          {busy ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Adding…
            </>
          ) : (
            <>
              <Key className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Add a passkey
            </>
          )}
        </Button>
      </div>

      {!hasWebAuthn && (
        <p className="text-xs text-muted-foreground" role="note">
          This browser doesn&apos;t support passkeys. Try Chrome, Safari, Edge, or Firefox on a
          device with a biometric.
        </p>
      )}

      {add.isError && (
        <p className="text-xs text-destructive" role="alert">
          {formatAddPasskeyError(add.error)}
        </p>
      )}

      <PasskeyList passkeys={passkeys.data ?? []} loading={passkeys.isLoading} />
    </div>
  );
}

function PasskeyList({
  passkeys,
  loading,
}: {
  passkeys: Passkey[];
  loading: boolean;
}): JSX.Element {
  const del = useDeletePasskey();

  if (loading) {
    return <Skeleton className="h-16 w-full" />;
  }
  if (passkeys.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border-subtle bg-surface-2/50 p-3 text-center text-xs text-muted-foreground">
        No passkeys yet.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {passkeys.map((p) => (
        <li
          key={p.credential_id}
          className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/40 px-3 py-2"
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm text-foreground">{p.name ?? "Unnamed passkey"}</span>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              {p.attachment === "platform"
                ? "This device"
                : p.attachment === "cross-platform"
                  ? "Security key or phone"
                  : "Unknown"}
              {p.created_at && (
                <>
                  <span aria-hidden>·</span>
                  Added {formatShortDate(p.created_at)}
                </>
              )}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove passkey ${p.name ?? p.credential_id}`}
            disabled={del.isPending && del.variables === p.credential_id}
            onClick={() => del.mutate(p.credential_id)}
          >
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </li>
      ))}
    </ul>
  );
}

// --- Authenticator app (TOTP) card --------------------------------------

function AuthenticatorCard({ totpEnabled }: { totpEnabled: boolean }): JSX.Element {
  const [enrollOpen, setEnrollOpen] = React.useState(false);
  const disable = useTOTPDisable();

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-md border border-border-subtle bg-surface-2 p-2 text-muted-foreground">
          <Smartphone className="h-4 w-4" aria-hidden />
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-foreground">Authenticator app</h3>
          <p className="text-xs text-muted-foreground">
            Time-based codes from an app on your phone (Google Authenticator, 1Password, etc.).
          </p>
          <div className="mt-1 flex items-center gap-1.5 text-xs">
            {totpEnabled ? (
              <>
                <ShieldCheck className="h-3.5 w-3.5 text-state-cached-fg" aria-hidden />
                <span className="text-state-cached-fg">Enrolled</span>
              </>
            ) : (
              <>
                <ShieldOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                <span className="text-muted-foreground">Not enrolled</span>
              </>
            )}
          </div>
          {disable.isError && (
            <p className="text-xs text-destructive" role="alert">
              Couldn&apos;t disable the authenticator. Try again.
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {totpEnabled ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disable.isPending}
            onClick={() => disable.mutate()}
            className={cn(disable.isPending && "opacity-70")}
          >
            {disable.isPending ? "Disabling…" : "Turn off"}
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setEnrollOpen(true)}>
            Set up
          </Button>
        )}
        <TotpEnrollDialog open={enrollOpen} onOpenChange={setEnrollOpen} />
      </div>
    </div>
  );
}

// --- Utility -----------------------------------------------------------

function SectionHeading({ id, title }: { id: string; title: string }): JSX.Element {
  return (
    <h2 id={id} className="font-display text-base font-semibold tracking-tight">
      {title}
    </h2>
  );
}

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Turn the ``useAddPasskey`` error into human copy.
 *
 * WebAuthn ``navigator.credentials.create()`` rejects with a
 * DOMException whose ``.name`` distinguishes cancel from unsupported —
 * the SPA should be gentle on cancel (user tapped "Cancel" on the OS
 * prompt) and clearer on hard failures. Backend errors from the two
 * proxy round-trips ride the standard ``ApiErrorBody`` shape.
 */
function formatAddPasskeyError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError") {
      return "Registration was cancelled or timed out. Try again when ready.";
    }
    if (err.name === "InvalidStateError") {
      return "This device is already registered as a passkey on this account.";
    }
    if (err.name === "NotSupportedError") {
      return "This browser or device doesn't support passkey registration.";
    }
  }
  if (err && typeof err === "object" && "message" in err) {
    // Backend proxy failures (rate limited, WebAuthn not enabled, etc.)
    // carry the ApiErrorBody message from the standard fetch wrapper.
    return String((err as { message: unknown }).message);
  }
  return "Couldn't add the passkey. Try again.";
}
